import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useRemembered } from '../lib/remember';

// Every Fold on the page, so Expand all / Collapse all (Kristi, Oct 28) can set them at once.
const folds = new Map<string, { open: boolean; set: (v: boolean) => void }>();
const watchers = new Set<() => void>();
const changed = () => watchers.forEach((w) => w());

/** A section that folds; open or closed is remembered on this device (shared by every book). */
export function Fold({
  id,
  title,
  hint,
  open: defaultOpen,
  label,
  children,
}: {
  id: string;
  title: string;
  hint?: string;
  open: boolean;
  /** The section's name for screen readers, when it differs from its title. */
  label?: string;
  children: ComponentChildren;
}) {
  const [open, setOpen] = useRemembered(`book.fold.${id}`, defaultOpen);
  useEffect(() => {
    folds.set(id, { open, set: setOpen });
    changed();
  });
  useEffect(
    () => () => {
      folds.delete(id);
      changed();
    },
    [id],
  );
  return (
    <section class="bk-fold" aria-label={label ?? title}>
      <h3 class="bk-fold-head">
        <button type="button" aria-expanded={open} onClick={() => setOpen(!open)}>
          <span class="bk-arrow" aria-hidden="true">
            {open ? '▾' : '▸'}
          </span>
          {title}
          {!open && hint ? <span class="bk-hint"> · {hint}</span> : null}
        </button>
      </h3>
      {open ? <div class="bk-fold-body">{children}</div> : null}
    </section>
  );
}

/**
 * One small link above the folding sections (Kristi, Oct 28; like Shelves' Collapse all): it says
 * Collapse all while any section is open, Expand all once they're all folded. Each section still
 * folds on its own, and both are remembered on this device.
 */
export function FoldAll() {
  const [, tick] = useState(0);
  useEffect(() => {
    const w = () => tick((n) => n + 1);
    watchers.add(w);
    return () => void watchers.delete(w);
  }, []);
  const all = [...folds.values()];
  if (!all.length) return null;
  const anyOpen = all.some((f) => f.open);
  return (
    <div class="bk-foldall">
      <button type="button" class="btn-link" onClick={() => all.forEach((f) => f.set(!anyOpen))}>
        {anyOpen ? 'Collapse all' : 'Expand all'}
      </button>
    </div>
  );
}
